import React from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { SearchableOption } from '@/components/shared/SearchableSelect';
import { normalizeFaSearchText } from '@/lib/textNormalize';
import type { HeadingFlat } from '@/document-entry/headingCatalog';

export type PickerTab = 'general' | 'subsidiary' | 'detail' | 'project' | 'person';

export type RowPickerDraft = {
  accountCode: string;
  projectId: string;
  beneficiaryKey: string;
  filterGeneralCode: string;
  filterSubsidiaryCode: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: RowPickerDraft;
  onDraftChange: (patch: Partial<RowPickerDraft>) => void;
  onConfirm: () => void;
  headingCatalog: HeadingFlat[];
  projectOptions: SearchableOption[];
  beneficiaryOptions: SearchableOption[];
  initialSearch?: string;
  /** فقط تب‌های کل / معین / تفصیلی؛ انتخاب هر سطح مجاز است */
  accountOnly?: boolean;
  loadingHeadingCatalog?: boolean;
  loadingProjects?: boolean;
};

function PickerLoadingState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-3 py-10 text-slate-500">
      <Loader2 className="h-6 w-6 animate-spin text-sky-600" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

const TAB_LABELS: Record<PickerTab, string> = {
  general: 'کل',
  subsidiary: 'معین',
  detail: 'تفصیلی',
  project: 'پروژه',
  person: 'شخص',
};

const ACCOUNT_WITH_PROJECT_TABS: PickerTab[] = ['general', 'subsidiary', 'detail', 'project'];
const FULL_TABS: PickerTab[] = ['general', 'subsidiary', 'detail', 'project', 'person'];

const RowAccountPickerDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  draft,
  onDraftChange,
  onConfirm,
  headingCatalog,
  projectOptions,
  beneficiaryOptions,
  initialSearch = '',
  accountOnly = false,
  loadingHeadingCatalog = false,
  loadingProjects = false,
}) => {
  const visibleTabs = accountOnly ? ACCOUNT_WITH_PROJECT_TABS : FULL_TABS;
  const [tab, setTab] = React.useState<PickerTab>('general');
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setSearch(initialSearch);
    setTab('general');
  }, [open, initialSearch]);

  const generalRows = React.useMemo(
    () => headingCatalog.filter((x) => x.level === 'general'),
    [headingCatalog],
  );
  const subsidiaryRows = React.useMemo(
    () =>
      headingCatalog.filter(
        (x) => x.level === 'subsidiary' && (!draft.filterGeneralCode || x.parentCode === draft.filterGeneralCode),
      ),
    [headingCatalog, draft.filterGeneralCode],
  );
  const detailRows = React.useMemo(
    () =>
      headingCatalog.filter(
        (x) => x.level === 'detail' && (!draft.filterSubsidiaryCode || x.parentCode === draft.filterSubsidiaryCode),
      ),
    [headingCatalog, draft.filterSubsidiaryCode],
  );

  const accountRows = React.useMemo(() => {
    if (tab === 'general') return generalRows;
    if (tab === 'subsidiary') return subsidiaryRows;
    if (tab === 'detail') return detailRows;
    return [];
  }, [tab, generalRows, subsidiaryRows, detailRows]);

  const filteredAccountRows = React.useMemo(() => {
    const s = normalizeFaSearchText(search);
    if (!s) return accountRows;
    return accountRows.filter((x) => normalizeFaSearchText(`${x.code} ${x.title}`).includes(s));
  }, [accountRows, search]);

  const filteredProjects = React.useMemo(() => {
    const s = normalizeFaSearchText(search);
    const list = projectOptions.filter((x) => x.value !== '');
    if (!s) return list;
    return list.filter((x) => normalizeFaSearchText(`${x.label} ${x.searchText || ''}`).includes(s));
  }, [projectOptions, search]);

  const filteredPeople = React.useMemo(() => {
    const s = normalizeFaSearchText(search);
    const list = beneficiaryOptions.filter((x) => x.value !== '');
    if (!s) return list;
    return list.filter((x) => normalizeFaSearchText(`${x.label} ${x.searchText || ''}`).includes(s));
  }, [beneficiaryOptions, search]);

  const selectAccount = (code: string, level: HeadingFlat['level']) => {
    if (accountOnly) {
      if (level === 'general') {
        onDraftChange({ accountCode: code, filterGeneralCode: code, filterSubsidiaryCode: '' });
      } else if (level === 'subsidiary') {
        onDraftChange({ accountCode: code, filterSubsidiaryCode: code });
      } else {
        onDraftChange({ accountCode: code });
      }
      return;
    }

    if (level === 'general') {
      onDraftChange({ accountCode: code, filterGeneralCode: code, filterSubsidiaryCode: '' });
      setTab('subsidiary');
      return;
    }
    if (level === 'subsidiary') {
      onDraftChange({ accountCode: code, filterSubsidiaryCode: code });
      setTab('detail');
      return;
    }
    onDraftChange({ accountCode: code });
    if (accountOnly) setTab('project');
  };

  const canConfirm = Boolean(draft.accountCode.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto [&>button]:left-4 [&>button]:right-auto">
        <DialogHeader>
          <DialogTitle>{accountOnly ? 'انتخاب حساب و پروژه' : 'انتخاب حساب، پروژه و طرف حساب'}</DialogTitle>
          <DialogDescription className="sr-only">
            {accountOnly
              ? 'حساب کل، معین یا تفصیلی و در صورت نیاز پروژه را انتخاب کنید.'
              : 'حساب، پروژه و طرف حساب را انتخاب کنید.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {visibleTabs.map((t) => (
            <Button key={t} type="button" size="sm" variant={tab === t ? 'default' : 'outline'} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
            </Button>
          ))}
        </div>

        <div className="rounded-md border bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <div>
            <span className="text-slate-500">کد حساب انتخاب‌شده: </span>
            <span className="font-mono font-medium">{draft.accountCode || '—'}</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            <span>
              <span className="text-slate-500">پروژه: </span>
              {projectOptions.find((p) => p.value === draft.projectId)?.label || '—'}
            </span>
            {!accountOnly ? (
              <span>
                <span className="text-slate-500">شخص: </span>
                {beneficiaryOptions.find((b) => b.value === draft.beneficiaryKey)?.label || '—'}
              </span>
            ) : null}
          </div>
        </div>

        {(tab === 'subsidiary' || tab === 'detail') && (
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-slate-600">فیلتر حساب کل</label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={draft.filterGeneralCode}
                onChange={(e) =>
                  onDraftChange({ filterGeneralCode: e.target.value, filterSubsidiaryCode: '' })
                }
              >
                <option value="">همه</option>
                {generalRows.map((g) => (
                  <option key={g.code} value={g.code}>
                    {g.code} — {g.title}
                  </option>
                ))}
              </select>
            </div>
            {tab === 'detail' ? (
              <div>
                <label className="mb-1 block text-xs text-slate-600">فیلتر حساب معین</label>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={draft.filterSubsidiaryCode}
                  onChange={(e) => onDraftChange({ filterSubsidiaryCode: e.target.value })}
                >
                  <option value="">همه</option>
                  {subsidiaryRows.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.code} — {s.title}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
        )}

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            tab === 'project'
              ? 'جستجوی پروژه…'
              : tab === 'person'
                ? 'جستجوی شخص / شرکت / کارمند…'
                : `جستجو در حساب‌های ${TAB_LABELS[tab]}…`
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter' && tab !== 'project' && tab !== 'person') {
              const first = filteredAccountRows[0];
              if (first) selectAccount(first.code, first.level);
            }
          }}
        />

        <div className="max-h-[42vh] overflow-y-auto rounded-md border">
          {tab === 'project' ? (
            loadingProjects ? (
              <PickerLoadingState message="در حال بارگذاری پروژه‌ها…" />
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  <tr
                    className="cursor-pointer border-b hover:bg-slate-50"
                    onClick={() => onDraftChange({ projectId: '' })}
                  >
                    <td className="px-3 py-2 text-slate-500">بدون پروژه</td>
                  </tr>
                  {filteredProjects.map((p) => (
                    <tr
                      key={p.value}
                      className={`cursor-pointer border-b hover:bg-slate-50 ${draft.projectId === p.value ? 'bg-emerald-50' : ''}`}
                      onClick={() => onDraftChange({ projectId: p.value })}
                    >
                      <td className="px-3 py-2">{p.label}</td>
                    </tr>
                  ))}
                  {filteredProjects.length === 0 ? (
                    <tr>
                      <td className="px-3 py-4 text-center text-slate-500">پروژه‌ای پیدا نشد.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            )
          ) : tab === 'person' ? (
            <table className="w-full text-sm">
              <tbody>
                <tr
                  className="cursor-pointer border-b hover:bg-slate-50"
                  onClick={() => onDraftChange({ beneficiaryKey: '' })}
                >
                  <td className="px-3 py-2 text-slate-500">بدون طرف حساب</td>
                </tr>
                {filteredPeople.map((b) => (
                  <tr
                    key={b.value}
                    className={`cursor-pointer border-b hover:bg-slate-50 ${draft.beneficiaryKey === b.value ? 'bg-emerald-50' : ''}`}
                    onClick={() => onDraftChange({ beneficiaryKey: b.value })}
                  >
                    <td className="px-3 py-2">{b.label}</td>
                  </tr>
                ))}
                {filteredPeople.length === 0 ? (
                  <tr>
                    <td className="px-3 py-4 text-center text-slate-500">طرفی پیدا نشد.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          ) : loadingHeadingCatalog ? (
            <PickerLoadingState message="در حال بارگذاری فهرست حساب‌ها…" />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-slate-700">
                  <th className="border px-2 py-2 text-right">کد</th>
                  <th className="border px-2 py-2 text-right">عنوان</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccountRows.map((x) => (
                  <tr
                    key={`${x.level}-${x.code}`}
                    className={`cursor-pointer hover:bg-slate-50 ${draft.accountCode === x.code ? 'bg-emerald-50' : ''}`}
                    onClick={() => selectAccount(x.code, x.level)}
                    onDoubleClick={() => {
                      onDraftChange({ accountCode: x.code });
                      onConfirm();
                    }}
                  >
                    <td className="border px-2 py-2 font-mono">{x.code}</td>
                    <td className="border px-2 py-2">{x.title}</td>
                  </tr>
                ))}
                {filteredAccountRows.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="border px-2 py-4 text-center text-slate-500">
                      {headingCatalog.length === 0
                        ? 'فهرست حساب‌ها خالی است. کد حساب را به‌صورت دستی وارد کنید.'
                        : 'موردی پیدا نشد.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-xs text-slate-500">
          {accountOnly
            ? 'پس از انتخاب حساب تفصیلی، تب پروژه باز می‌شود. پروژه در ستون جداگانهٔ «پروژه» ذخیره می‌شود.'
            : 'جستجو در همه سطوح حساب انجام می‌شود. در تب‌های کل و معین یک‌بار کلیک برای فیلتر مرحله بعد؛ دوبار کلیک روی تفصیلی انتخاب نهایی است. Enter روی جستجو اولین حساب را انتخاب می‌کند.'}
        </p>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            انصراف
          </Button>
          <Button type="button" onClick={onConfirm} disabled={!canConfirm}>
            تأیید
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RowAccountPickerDialog;
